import { useQuery } from '@tanstack/react-query';
import { DollarSign, TrendingUp, Clock, Users, Zap, Shield, Target, ArrowDown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ExecutiveRoiPage() {
  const token = localStorage.getItem('nexus_token');
  const { data: roi } = useQuery({
    queryKey: ['executive-roi'],
    queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/executive/roi`, { headers: { Authorization: `Bearer ${token}` } }); return data; },
  });

  if (!roi) return <div className="w-full max-w-[1200px] mx-auto"><div className="h-96 bg-[var(--nexus-bg-elevated)] rounded-xl animate-pulse" /></div>;
  const h = roi.headline;
  const ba = roi.before_after;

  return (
    <div data-testid="executive-roi" className="w-full max-w-[1200px] mx-auto">
      <div className="mb-4">
        <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)]">Executive ROI Dashboard</h1>
        <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">Business impact of NEXUS ITSM AI agents</p>
      </div>

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <KpiBox label="Cost Saved" value={`$${(h.cost_saved / 1000).toFixed(0)}k`} icon={DollarSign} color="var(--nexus-success)" />
        <KpiBox label="Auto Resolution" value={`${h.auto_resolution_rate}%`} icon={Target} color="var(--nexus-accent)" />
        <KpiBox label="MTTR" value={`${h.mttr_minutes} min`} icon={Clock} color="var(--nexus-warning)" />
        <KpiBox label="Incidents Resolved" value={h.incidents_resolved} icon={Zap} color="var(--nexus-primary-bright)" />
        <KpiBox label="FTE Saved" value={`${h.fte_saved}`} icon={Users} color="var(--nexus-info)" />
      </div>

      {/* Before / After */}
      <div className="nexus-card p-6 mb-4">
        <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-4">Before NEXUS vs After NEXUS</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <CompareBox label="Mean Time to Resolution" before={`${ba.mttr.before} min (4 hours)`} after={`${ba.mttr.after} min`} improvement={`${ba.mttr.improvement_pct}% faster`} />
          <CompareBox label="Auto Resolution Rate" before="0% (all manual)" after={`${ba.auto_rate.after}%`} improvement={`${ba.auto_rate.after}% automated`} />
          <CompareBox label="Monthly Cost" before={`$${(ba.cost.before_monthly / 1000).toFixed(0)}k`} after={`$${(ba.cost.after_monthly / 1000).toFixed(0)}k`} improvement={`$${(ba.cost.savings_monthly / 1000).toFixed(0)}k/mo saved`} />
        </div>
      </div>

      {/* Agent Fleet */}
      <div className="nexus-card p-4">
        <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-2">AI Agent Fleet</h2>
        <div className="grid grid-cols-3 gap-3">
          <div className="p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
            <p className="text-[10px] text-[var(--nexus-text-muted)]">Active Agents</p>
            <p className="font-heading text-xl font-bold text-[var(--nexus-accent)]">{roi.agents.total}</p>
          </div>
          <div className="p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
            <p className="text-[10px] text-[var(--nexus-text-muted)]">Security Events</p>
            <p className="font-heading text-xl font-bold text-[var(--nexus-danger)]">{roi.agents.security_events}</p>
          </div>
          <div className="p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
            <p className="text-[10px] text-[var(--nexus-text-muted)]">Changes Managed</p>
            <p className="font-heading text-xl font-bold text-[var(--nexus-warning)]">{roi.agents.changes_managed}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const KpiBox = ({ label, value, icon: Icon, color }) => (
  <div className="nexus-card p-4 text-center">
    <Icon size={20} style={{ color }} className="mx-auto mb-1" />
    <p className="font-heading text-2xl font-bold tabular-nums" style={{ color }}>{value}</p>
    <p className="text-[9px] text-[var(--nexus-text-muted)] uppercase tracking-wider mt-1">{label}</p>
  </div>
);

const CompareBox = ({ label, before, after, improvement }) => (
  <div className="text-center">
    <p className="text-[10px] text-[var(--nexus-text-muted)] uppercase tracking-wider mb-2">{label}</p>
    <div className="flex items-center justify-center gap-3">
      <div className="text-right">
        <p className="text-[10px] text-[var(--nexus-text-muted)]">Before</p>
        <p className="font-mono text-sm text-[var(--nexus-danger)]">{before}</p>
      </div>
      <ArrowDown size={16} className="text-[var(--nexus-success)] rotate-[-90deg]" />
      <div className="text-left">
        <p className="text-[10px] text-[var(--nexus-text-muted)]">After</p>
        <p className="font-mono text-sm text-[var(--nexus-success)]">{after}</p>
      </div>
    </div>
    <p className="text-[10px] font-semibold text-[var(--nexus-success)] mt-1">{improvement}</p>
  </div>
);
